import {
  ErrorFallback,
  SideFilters,
  SideFiltersSkeleton,
  SuspenseWithSentryErrorBoundary,
} from '@retail/components';
import { CentralAssortmentFilters } from '@retail/my-assortment/components';
import { useFetchCentralAssortmentMemberView } from '@retail/my-assortment/data-access';
import { AssortmentItemIdentifiersFilterDialog } from '@retail/products/containers';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { useDisclosure } from '@shared/hooks';
import { useCentralAssortmentMemberViewFilterOptions } from './useCentralAssortmentMemberViewFilterOptions';

interface Props {
  showFilters: boolean;
}

export function CentralAssortmentFiltersMemberView({ showFilters }: Props) {
  return (
    <SuspenseWithSentryErrorBoundary
      suspenseFallback={
        <SideFiltersSkeleton showFilters={showFilters} filterCount={7} sx={{ py: 1 }} />
      }
      errorFallback={(fallbackProps) => (
        <SideFilters showFilters={showFilters} sx={{ py: 1 }}>
          <ErrorFallback {...fallbackProps} message={fallbackProps.error.message} />
        </SideFilters>
      )}
    >
      <CentralAssortmentFiltersMemberViewPanel showFilters={showFilters} />
    </SuspenseWithSentryErrorBoundary>
  );
}

function CentralAssortmentFiltersMemberViewPanel({ showFilters }: Props) {
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const { userCanRead } = usePermissionsForCurrentUser();
  const itemIdentifiersFilterDialog = useDisclosure(false);
  const { suppliers, productGroupHierarchy, subAssortments, selectedSubAssortments, setFilters } =
    useCentralAssortmentMemberViewFilterOptions();

  return (
    <>
      <CentralAssortmentFilters
        showFilters={showFilters}
        suppliers={suppliers}
        productGroupHierarchy={productGroupHierarchy}
        assortmentTags={userCanRead('SUB_ASSORTMENTS') ? subAssortments : undefined}
        selectedSubAssortments={selectedSubAssortments}
        hideIsTagged={true}
        showFromMainSupplier={appTenant.hasMainSupplier}
        onOpenItemIdentifiersFilter={itemIdentifiersFilterDialog.onOpen}
      />
      {itemIdentifiersFilterDialog.isOpen && (
        <AssortmentItemIdentifiersFilterDialog
          assortmentId={assortmentId}
          useFetchItems={useFetchCentralAssortmentMemberView}
          onApply={(itemIdentifiers) => {
            setFilters({ itemIdentifiers });
            itemIdentifiersFilterDialog.onClose();
          }}
          onClose={itemIdentifiersFilterDialog.onClose}
        />
      )}
    </>
  );
}
